import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { Button, Typography, debounce } from '@mui/material';
import * as Styles from './IncrementDecrement.styles';
import theme from 'App.theme';

type IncrementDecrementProps = {
  label: string;
  count: number;
  limit?: number;
  debounceInput?: boolean;
  disabled?: boolean;
  onChange?: (count: number) => void;
};

const IncrementDecrement: React.FC<IncrementDecrementProps> = ({
  label,
  count,
  limit,
  debounceInput,
  onChange,
  disabled = !onChange,
}) => {
  const [value, _setValue] = useState<string>('' + (count || 0));

  useEffect(() => {
    _setValue('' + (count || 0));
  }, [count]);

  const handleChange = useCallback((count: string) => onChange?.(+count), [onChange]);
  const onChangeDebounced = useMemo(
    () => (debounceInput ? debounce(handleChange, 750) : handleChange),
    [debounceInput, handleChange],
  );

  const setValue = useCallback(
    (value: string) => {
      if (disabled) return;
      value = value.replace(/[^\d]/g, '') || '0';
      if (limit != null && +value > limit) value = '' + limit;
      _setValue(value);
      onChangeDebounced(value);
    },
    [disabled, limit, onChangeDebounced],
  );

  const increment = useCallback(() => {
    setValue('' + (+value + 1));
  }, [setValue, value]);

  const decrement = useCallback(() => {
    if (+value > 0) setValue('' + (+value - 1));
  }, [setValue, value]);

  const leftCanMove = !disabled && +value > 0;
  const rightCanMove = !disabled && (limit == null || +value < limit);
  const inputId = `${label}-input`;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: theme.layout.spacing.medium,
      }}
    >
      <Typography variant="body2" style={{ flex: 1 }}>
        <label htmlFor={inputId}>{label}</label>
      </Typography>

      <div
        style={{
          flex: 1,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: theme.layout.spacing.tiny,
        }}
      >
        <Styles.IconDiv side="left" canMove={leftCanMove}>
          <Button name="decrement" color="inherit" disabled={!leftCanMove} onClick={decrement} tabIndex={-1}>
            {disabled ? null : <RemoveIcon />}
          </Button>
        </Styles.IconDiv>

        <Styles.Input
          id={inputId}
          type="number"
          name={label} // TODO: SS for testing, remove once we have proper tests
          value={value}
          min={0}
          max={9999}
          onChange={e => setValue(e.target.value)}
          disabled={disabled}
        />

        <Styles.IconDiv side="right" canMove={rightCanMove}>
          <Button name="increment" color="inherit" disabled={!rightCanMove} onClick={increment} tabIndex={-1}>
            {disabled ? null : <AddIcon />}
          </Button>
        </Styles.IconDiv>
      </div>
    </div>
  );
};

export default IncrementDecrement;
